import { Stack } from '@mantine/core';
import PermissionBox from 'components/inputs/permission-input/PermissionBox';
import { groupBy, uniq } from 'lodash';
import { usePermissionDataProvider } from 'model/PermissionDataProvider';
import { useEffect, useMemo } from 'react';
import {
  ALL_ORGANIZATION_MODULES_ENABLED,
  isPermissionEnabledForOrganization,
  OrganizationModuleFlags,
} from 'features/organization-modules/use-organization-modules';

const NO_INHERITED_PERMISSIONS: number[] = [];

interface PermissionInputProps {
  permissionType: 'global' | 'organization' | 'department';
  value: number[];
  onChange: (value: number[]) => void;
  moduleFlags?: OrganizationModuleFlags;
  inheritedPermissions?: number[];
}

/**
 * Displays all available permissions and allows to check/uncheck them.
 */
export default function PermissionInput({
  permissionType,
  value,
  onChange,
  moduleFlags = ALL_ORGANIZATION_MODULES_ENABLED,
  inheritedPermissions = NO_INHERITED_PERMISSIONS,
}: PermissionInputProps) {
  const { departmentPermissions, globalPermissions, organizationPermissions } = usePermissionDataProvider();
  const permissions = useMemo(
    () =>
      (permissionType === 'global'
        ? globalPermissions
        : permissionType === 'department'
        ? departmentPermissions
        : permissionType === 'organization'
        ? organizationPermissions
        : []
      ).filter(({ slug }) => isPermissionEnabledForOrganization(slug, moduleFlags)),
    [departmentPermissions, globalPermissions, moduleFlags, organizationPermissions, permissionType]
  );

  const allPermissions = useMemo(() => groupBy(permissions, 'permissionGroup'), [permissions]);
  const availablePermissionIds = useMemo(
    () => new Set(permissions.map(({ permissionId }) => permissionId)),
    [permissions]
  );
  const inheritedPermissionIds = useMemo(() => new Set(inheritedPermissions), [inheritedPermissions]);

  useEffect(() => {
    const availableValue = value.filter((permissionId) => availablePermissionIds.has(permissionId));

    if (availableValue.length !== value.length) {
      onChange(availableValue);
    }
  }, [availablePermissionIds, onChange, value]);

  /**
   * Checks if the permission is checked.
   */
  const hasPermission = (permissionId: number) =>
    value.includes(permissionId) || inheritedPermissionIds.has(permissionId);

  /**
   * Add multiple permissions.
   */
  const addPermissions = (permissions: number[]) => {
    onChange(uniq([...value, ...permissions.filter((permissionId) => !inheritedPermissionIds.has(permissionId))]));
  };

  /**
   * Remove multiple permissions.
   */
  const removePermissions = (permissions: number[]) => {
    onChange(value.filter((id) => !permissions.includes(id)));
  };

  /**
   * Toggles permission.
   */
  const setPermission = (permissionId: number, checked: boolean) => {
    if (checked) {
      addPermissions([permissionId]);
    } else {
      removePermissions([permissionId]);
    }
  };

  return (
    <Stack spacing={12}>
      {Object.entries(allPermissions).map(([permissionGroup, permissions]) => (
        <PermissionBox
          key={permissionGroup}
          permissionGroup={permissionGroup}
          permissions={permissions}
          setPermission={setPermission}
          hasPermission={hasPermission}
          removePermissions={removePermissions}
          addPermissions={addPermissions}
          inheritedPermissions={inheritedPermissions}
        />
      ))}
    </Stack>
  );
}
